import React, { useState, useEffect, useMemo } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Typography, Box, TablePagination, InputAdornment, Chip, 
  Card, MenuItem, FormControl, InputLabel, Select
} from '@mui/material';
import { 
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, 
  Search as SearchIcon, CloudDone as CloudDoneIcon 
} from '@mui/icons-material';
import { 
  collection, addDoc, updateDoc, deleteDoc, doc, 
  onSnapshot, query, orderBy, serverTimestamp 
} from "firebase/firestore";
import { db } from "../../firebase";

const initialForm = { id: "", category: "", amount: "", tenure: "", interest: "", status: "Active" };

export default function LoanDetails() {
  const [loans, setLoans] = useState([]);
  const [categories, setCategories] = useState([]); // State for Select Box options
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(initialForm);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  

  // 1. FETCH LOAN DATA & CATEGORIES
  useEffect(() => {
    // Fetch Main Loan Records
    const qLoans = query(collection(db, "loans"), orderBy("createdAt", "desc"));
    const unsubLoans = onSnapshot(qLoans, (snapshot) => {
      setLoans(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    // Fetch Categories for the Select Box
    const qCats = query(collection(db, "loan_categories"), orderBy("category", "asc"));
    const unsubCats = onSnapshot(qCats, (snapshot) => {
      setCategories(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    return () => { unsubLoans(); unsubCats(); };
  }, []);

  // 2. SEARCH LOGIC
  const filteredLoans = useMemo(() => {
    return loans.filter((loan) =>
      loan.category?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [loans, searchQuery]);

  // 3. HANDLERS
  const handleOpen = (loan = initialForm) => {
    setFormData(loan);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData(initialForm);
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        category: formData.category,
        amount: Number(formData.amount),
        tenure: Number(formData.tenure),
        interest: Number(formData.interest),
        status: formData.status,
        updatedAt: serverTimestamp()
      };

      if (formData.id) {
        // UPDATE Logic
        await updateDoc(doc(db, "loans", formData.id), payload);
      } else {
        // ADD Logic
        await addDoc(collection(db, "loans"), { ...payload, createdAt: serverTimestamp() });
      }
      handleClose();
    } catch (err) { console.error("Error saving:", err); }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure?")) await deleteDoc(doc(db, "loans", id));
  };

  return (
    <Box sx={{ p: { xs: 2, md: 1 },  margin: '0 auto' }}>
      <Card sx={{ p: 2, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
        
        {/* TOP BAR */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Loan Entries</Typography>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField 
              size="small"
              placeholder="Search category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'gray' }} /> }}
            />
            <Button 
              variant="contained" 
              size="small" 
              startIcon={<AddIcon />} 
              onClick={() => handleOpen()}
              sx={{ borderRadius: 1.5, textTransform: 'none' }}
            >
              New Entry
            </Button>
          </Box>
        </Box>

        {/* TABLE */}
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #f0f0f0' }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: '#fafafa' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Sr.No</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Intrest</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Tenure</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLoans.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((loan, index) => (
                <TableRow key={loan.id} hover>
                  <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell><b>{loan.category}</b></TableCell>
                  <TableCell>{loan.amount?.toLocaleString()}</TableCell>
                  <TableCell>{loan.interest}%</TableCell>
                  <TableCell>{loan.tenure} Months</TableCell>
                  <TableCell>
                    <Chip label={loan.status} size="small" color={loan.status === 'Active' ? 'success' : 'default'} variant="outlined" />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" color="primary" onClick={() => handleOpen(loan)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(loan.id)}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10]}
            component="div"
            count={filteredLoans.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          />
        </TableContainer>
      </Card>

      {/* POPUP FORM */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
          {formData.id ? "Update Loan" : "Create New Loan"}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, py: 1 }}>
            
            {/* CATEGORY SELECT BOX */}
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                label="Category"
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.category}>
                    {cat.category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField 
              label="Amount" 
              size="small" 
              type="number" 
              fullWidth 
              value={formData.amount} 
              onChange={(e) => setFormData({...formData, amount: e.target.value})} 
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField 
                label="Tenure (Months)" 
                size="small" 
                type="number" 
                fullWidth 
                value={formData.tenure} 
                onChange={(e) => setFormData({...formData, tenure: e.target.value})} 
              />
              <TextField 
                label="Interest %" 
                size="small" 
                type="number" 
                fullWidth 
                value={formData.interest} 
                onChange={(e) => setFormData({...formData, interest: e.target.value})} 
              />
            </Box>

            <TextField 
              select 
              label="Status" 
              size="small" 
              fullWidth 
              value={formData.status} 
              onChange={(e) => setFormData({...formData, status: e.target.value})}
            >
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Closed">Closed</MenuItem>
            </TextField>

          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} color="inherit" size="small">Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} size="small" startIcon={<CloudDoneIcon />}>
            {formData.id ? "Update" : "Save Loan"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}